import { Box, Grid } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { Button, useRefresh } from "react-admin";
import sendRequest from "../../dataProvider/sendRequest";
import CustomButtonCancel from "../../unit/CustomButtonCancel";
import CustomButtonSubmit from "../../unit/CustomButtonSubmit";
import CustomDateTextInput from "../../unit/CustomDateTextInput";
import HeaderLabelTitle from "../../unit/HeaderLabelTitle";
import CustomNumberTextField from "../../unit/forms/CustomNumberTextField";
import useNotify from "../../unit/useNotify";
import CustomDialogTitle from "../../unit/CustomDialogTitle";
import PaperComponent from "../../unit/PaperComponent";

export default function CustomLoanOfflineDialog({ ...props }) {
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { appId, record: recordProp } = props;
  const lastDate = recordProp?.lastDate;
  const accruedInterestAmount = recordProp?.accruedInterestAmount;
  const curPayment = recordProp?.curPV;
  const curPaymentId = curPayment?.iId;
  const hasCurrentPaymentId =
    curPaymentId != undefined && curPaymentId != null && curPaymentId != "";
  const [open, setOpen] = React.useState(false);
  const [isSubmit, setIsSubmit] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [adjustPaymentAmount, setAdjustPaymentAmount] = React.useState<
    string | undefined
  >();
  const [adjustPaymentDate, setAdjustPaymentDate] = React.useState<
    string | Date
  >(new Date());
  const [adjustPaymentAmountError, setAdjustPaymentAmountError] =
    React.useState(false);
  const [adjustPaymentDateError, setAdjustPaymentDateError] =
    React.useState(false);
  const refresh = useRefresh();
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsSubmit(false);
    setIsSubmitting(false);
    // refresh();
  };

  React.useEffect(() => {
    if (adjustPaymentAmount && adjustPaymentDate) {
      setIsSubmit(true);
    } else {
      setIsSubmit(false);
    }
  }, [adjustPaymentAmount, adjustPaymentDate]);

  if (!hasCurrentPaymentId) {
    return (
      <Button
        label="Record Payment"
        variant="contained"
        size="medium"
        color="warning"
        sx={{ marginLeft: "10px", width: "175px" }}
        disabled
      />
    );
  }

  return (
    <React.Fragment>
      <Button
        id="button-loan-offline-record-payment"
        label="Record Payment"
        variant="contained"
        size="medium"
        color="warning"
        sx={{ marginLeft: "10px", width: "175px" }}
        onClick={handleClickOpen}
      />
      <Dialog
        id={`dialog-loan-offline-${appId}`}
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="md"
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        PaperProps={{
          component: "form",
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isSubmitting) {
              return false;
            }
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries((formData as any).entries());
            // console.log("formJson =====>", formJson);
            if (
              formJson.adjust_payment_amount == undefined ||
              formJson.adjust_payment_amount == "" ||
              formJson.adjust_payment_amount == null ||
              Number(formJson.adjust_payment_amount) == 0
            ) {
              setAdjustPaymentAmountError(true);
              errorNotify("Please enter the amount of the payment made");
              return false;
            } else {
              if (
                Number(formJson.adjust_payment_amount) <
                Number(formJson.accruedInterestAmount)
              ) {
                errorNotify(
                  "Payment amount cannot be less than the accrued interest amount " +
                    accruedInterestAmount
                );
                setAdjustPaymentAmountError(true);
                return false;
              } else {
                setAdjustPaymentAmountError(false);
              }
            }
            if (
              formJson.adjust_payment_date == undefined ||
              formJson.adjust_payment_date == "" ||
              formJson.adjust_payment_date == null
            ) {
              errorNotify("Please enter the date payment was made");
              setAdjustPaymentDateError(true);
              return false;
            } else {
              if (new Date(formJson.adjust_payment_date) < new Date(lastDate)) {
                errorNotify(
                  "Payment date cannot be before the last payment date " +
                    lastDate.slice(0, 10)
                );
                setAdjustPaymentDateError(true);
                return false;
              } else {
                setAdjustPaymentDateError(false);
              }
            }
            setIsSubmitting(true);
            try {
              const reqUrl = `loans/actions/offlinePayment`;
              const responseBack = await sendRequest(reqUrl, {
                id: curPaymentId,
                data: formJson,
              });
              if (responseBack?.code == 201 || responseBack?.code == "201") {
                errorNotify("An ACH payment is currently in progress.");
              } else if (
                responseBack?.code == 202 ||
                responseBack?.code == "202"
              ) {
                errorNotify(
                  "Payment amount need greater than Cumulated Interest, including over due"
                );
              } else if (
                responseBack?.code == 101 ||
                responseBack?.code == "101" ||
                responseBack?.code == 102 ||
                responseBack?.code == "102"
              ) {
                errorNotify("Payment Update Error");
              } else {
                successNotify("Payment Schedule updated !");
                handleClose();
                refresh();
              }
            } finally {
              setIsSubmitting(false);
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e5e5e5",
            mb: 2,
          }}
        >
          <CustomDialogTitle title={`Active Loan`} subTitle="Record Payment" />
        </DialogTitle>
        <DialogContent>
          <Box>
            <DialogContentText></DialogContentText>
            <Grid container sx={{ mb: 2, pb: 2 }}>
              <Grid sx={{ mb: 2 }} container>
                <Grid item xs={5} sx={{ pt: 1 }}>
                  <HeaderLabelTitle
                    fullWidth
                    title="Please enter the amount of the payment made"
                    required={true}
                  />
                </Grid>
                <Grid item xs={5}>
                  <CustomNumberTextField
                    fullWidth
                    type="text"
                    variant="standard"
                    id="adjust_payment_amount"
                    name="adjust_payment_amount"
                    value={adjustPaymentAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAdjustPaymentAmount(e.target.value)
                    }
                    error={adjustPaymentAmountError}
                    required={true}
                    helperText="Please enter the amount"
                  />
                </Grid>
                <Grid item xs={2}></Grid>
              </Grid>
              <Grid sx={{ mb: 2 }} container>
                <Grid item xs={5} sx={{ pt: 1 }}>
                  <HeaderLabelTitle
                    fullWidth
                    title="Please enter the date payment was made"
                    required={true}
                  />
                </Grid>
                <Grid item xs={5}>
                  <CustomDateTextInput
                    fullWidth
                    id="adjust_payment_date"
                    name="adjust_payment_date"
                    variant="standard"
                    value={adjustPaymentDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAdjustPaymentDate(e.target.value)
                    }
                    defaultValue={new Date()}
                    required={true}
                    error={adjustPaymentDateError}
                    helperText="no"
                  />
                </Grid>
                <Grid item xs={2}></Grid>
              </Grid>
              <Grid sx={{ mb: 2 }} container>
                <HeaderLabelTitle title="Add Note (Max 150 Characters)" />
              </Grid>
              <Grid sx={{ mb: 2 }} container>
                <TextField
                  id="update_scheduled_payment_comment"
                  name="update_scheduled_payment_comment"
                  placeholder="Notes"
                  multiline={true}
                  minRows={2}
                  fullWidth
                  variant="outlined"
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <CustomButtonCancel onClick={handleClose} />
          {isSubmit && (
            <CustomButtonSubmit
              label={isSubmitting ? "loading" : "Confirm"}
              disabled={isSubmitting}
            />
          )}
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
